<template>
  <vx-card class="mt-base" :title="activityTitle">
    <h4 class="mb-5">Hydraulics</h4>
    <p class="mb-5">Hydraulics is a very important area for Mechanical Engineers.</p>
    <p class="mb-5">
      Whether designing a steam engine, or working on a pump or turbine, Mechanical Engineers need
      to know how the water or liquid is going to move or operate. This allows them to create and
      maintain important machines that power our everyday world.
    </p>
    <p class="mb-5">You can learn more about hydraulics in the fun video below.</p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">Your Task:</h4>
    <p class="mb-5">
      Now, you know a little about how hydraulics work, it’s time to put that knowledge to good use
      making a hydraulic arm!
    </p>
    <p class="mb-5"><strong>You'll need: </strong></p>
    <h4 class="mb-5">MATERIALS:</h4>
    <ul class="default-list">
      <li>
        Cardboard (preferably a double corrugated board, although you could manage with a single,
        but this will mean a slightly weaker model.)
      </li>
      <li>10 ml Syringes x 8 (these syringes will act like the muscles of the hydraulic arm)</li>
      <li>2m long Tube (the tube needs to fit snugly into the opening of the syringe.)</li>
      <li>Toothpicks</li>
      <li>Superglue (you will need a lot!)</li>
    </ul>

    <h4 class="mb-5 mt-5">TOOLS:</h4>
    <ul class="default-list">
      <li>A utility knife (Stanley knife/box cutter)</li>
      <li>A pair of scissors</li>
    </ul>

    <p class="mb-5 mt-5">Before you start, there are a couple of things to be very careful with:</p>
    <ul class="numbered-list mb-5">
      <li>Using the Superglue</li>
      <li>Using the utility knife</li>
    </ul>

    <p class="mb-5">
      Taking the correct safety precautions is an important part of being a good mechanical
      engineer. So, be careful at all times!
    </p>
    <p class="mb-5">
      Once you’ve gathered up all the materials, it’s time to get started making your hydraulic arm!
    </p>
    <p class="mb-5">
      <strong><u>Step 1:</u></strong> Watch the overview video below. This will help you to get an
      idea of what you need to do to get started.
    </p>
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />

    <p class="mb-5">
      Now download the 3 documents below – these are the instructions you need to follow to create
      your hydraulic arm as well as the templates for the various parts you will need to cut our of
      cardboard.
    </p>
    <div class="vx-row mb-5">
      <div class="vx-col lg:w-1/3 w-full">
        <a :href="'content/engineering/robotic_arm_instructions.pdf' | blob_url" target="_blank">
          <centered-image-wrapper
            alt="Robotic arm instructions (PDF) (opens in a new tab)"
            :image="
              require('@/assets/images/experiences/engineering/hydraulic_arm_instructions_button.png')
            "
            classes="3/3"
          />
        </a>
        <p class="text-center">Hydraulic Arm Instruction Sheet</p>
      </div>
      <div class="vx-col lg:w-1/3 w-full">
        <a :href="'content/engineering/robot_arm_base_template.pdf' | blob_url" target="_blank">
          <centered-image-wrapper
            alt="Robot arm base template (PDF) (opens in a new tab)"
            :image="require('@/assets/images/experiences/engineering/base_template_button.png')"
            classes="3/3"
          />
        </a>
        <p class="text-center">Hydraulic Arm Base Template</p>
      </div>
      <div class="vx-col lg:w-1/3 w-full">
        <a :href="'content/engineering/robot_arm_parts_template.pdf' | blob_url" target="_blank">
          <centered-image-wrapper
            alt="Robot arm parts template (PDF) (opens in a new tab)"
            :image="
              require('@/assets/images/experiences/engineering/robot_arm_template_2_button.png')
            "
            classes="3/3"
          />
        </a>
        <p class="text-center">Hydraulic Arm Parts Template</p>
      </div>
    </div>

    <p class="mb-5">
      When you’ve completed your hydraulic arm, take a few photos or a video of your creation and
      upload these here. Ideally, try to take a video of your hydraulic arm performing some function
      or task – such as picking something up.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      filetype="media"
      upload-btn-text="Photo/Video 1"
      index="1"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      filetype="media"
      upload-btn-text="Photo/Video 2"
      index="2"
    />
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      filetype="media"
      upload-btn-text="Photo/Video 3"
      index="3"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/hydraulics_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/KEs_h-ucJEs',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/hydraulic_arm_overview_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/Eqn4g2aQ70U',
        },
      },
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    for (let i = 1; i <= 3; i++) {
      if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
        this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
      }
    }
  },
};
</script>
